import React from 'react'
import { Button } from './components/buttons/Button'
import { Badge } from './components/data-display/Badge'
import { Icon } from './Icon'
import { IMG } from './images'
import { Reveal, SectionHead } from './primitives'
import { PageHero, SERVICE_CATALOG, SERVICE_ORDER } from './serviceData'
import { CTASection } from './HomeSectionsB'

export function ServicesPage({ onNavigate }) {
  return (
    <div>
      <PageHero kicker="Our Services" crumbs={['Services']} onNavigate={onNavigate} image="facility"
        title="A full clean-energy & industrial technology architecture."
        sub="Six engineering segments, each a dedicated practice — explore the one that fits your operation." />

      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <div className="adr-seg-grid" style={{ display: 'grid', gap: 22 }}>
            {SERVICE_ORDER.map((key, i) => {
              const s = SERVICE_CATALOG[key];
              return (
                <Reveal key={key} variant="up" delay={(i % 3) * 0.07}>
                  <ServiceHubCard s={s} onClick={() => onNavigate({ page: 'Service', service: key })} />
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTASection onNavigate={onNavigate} />
    </div>
  );
}

export function ServiceHubCard({ s, onClick }) {
  const [h, setH] = React.useState(false);
  return (
    <div onClick={onClick} onMouseEnter={() => setH(true)} onMouseLeave={() => setH(false)} style={{
      cursor: 'pointer', height: '100%', borderRadius: 'var(--radius-xl)', overflow: 'hidden',
      background: 'var(--surface-card)', border: '1px solid var(--border-subtle)',
      boxShadow: h ? 'var(--glow-blue)' : 'var(--shadow-sm)', transform: h ? 'translateY(var(--lift))' : 'none',
      transition: 'transform var(--dur-med) var(--ease-out), box-shadow var(--dur-med) var(--ease-out)',
      display: 'flex', flexDirection: 'column',
    }}>
      <div style={{ position: 'relative', height: 160, overflow: 'hidden' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("${IMG(s.image)}")`, backgroundSize: 'cover', backgroundPosition: 'center', transform: h ? 'scale(1.07)' : 'none', transition: 'transform var(--dur-slow) var(--ease-out)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(8,27,51,0.1), rgba(8,27,51,0.7))' }} />
        <span style={{ position: 'absolute', left: 16, bottom: 16, width: 48, height: 48, borderRadius: 13, background: 'var(--energy)', color: 'var(--blue-900)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon name={s.icon} style={{ width: 24, height: 24 }} />
        </span>
      </div>
      <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', flex: 1 }}>
        <h3 style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h4)', color: 'var(--text-strong)', margin: '0 0 8px' }}>{s.name}</h3>
        <p style={{ margin: '0 0 18px', fontSize: 14.5, lineHeight: 1.55, color: 'var(--text-muted)', flex: 1 }}>{s.tagline}</p>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 14, color: 'var(--accent)' }}>
          Learn More <Icon name="arrow-right" style={{ width: 17, height: 17, transform: h ? 'translateX(4px)' : 'none', transition: 'transform var(--dur-fast)' }} />
        </span>
      </div>
    </div>
  );
}

// Same constants as ContactPage
const _EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const _PHONE_RE = /^\d{7,15}$/;

const _COUNTRY_CODES = [
  { code: '+91',  iso: 'IN' },
  { code: '+1',   iso: 'US' },
  { code: '+44',  iso: 'GB' },
  { code: '+971', iso: 'AE' },
  { code: '+966', iso: 'SA' },
  { code: '+65',  iso: 'SG' },
  { code: '+60',  iso: 'MY' },
  { code: '+61',  iso: 'AU' },
  { code: '+49',  iso: 'DE' },
  { code: '+33',  iso: 'FR' },
  { code: '+86',  iso: 'CN' },
  { code: '+81',  iso: 'JP' },
];
const _flagUrl = (iso) => `https://flagcdn.com/w20/${iso.toLowerCase()}.png`;

function _CountryDropdown({ value, onChange, hasError }) {
  const [open, setOpen] = React.useState(false);
  const ref = React.useRef(null);
  const selected = _COUNTRY_CODES.find((c) => c.code === value) || _COUNTRY_CODES[0];

  React.useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const borderColor = hasError ? 'var(--error, #e53935)' : open ? 'var(--accent)' : 'rgba(255,255,255,0.15)';
  const shadow = open && !hasError ? '0 0 0 4px var(--ring)' : 'none';

  return (
    <div ref={ref} style={{ position: 'relative', flexShrink: 0, width: 118 }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: '100%', height: 44, display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '0 10px', gap: 6, border: `1.5px solid ${borderColor}`, borderRadius: 'var(--radius-lg)',
          background: 'rgba(255,255,255,0.06)', cursor: 'pointer', outline: 'none',
          fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: '#fff',
          boxShadow: shadow, transition: 'border-color 150ms, box-shadow 150ms',
        }}
      >
        <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <img src={_flagUrl(selected.iso)} alt={selected.iso} width={20} height={15} style={{ borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} />
          <span>{selected.iso} {selected.code}</span>
        </span>
        <svg width="11" height="11" viewBox="0 0 12 12" fill="none" style={{ flexShrink: 0, opacity: 0.45, transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 200ms' }}>
          <path d="M2 4l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </button>

      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 4px)', left: 0, zIndex: 300,
          background: '#0d1e35', border: '1.5px solid rgba(255,255,255,0.15)',
          borderRadius: 'var(--radius-lg)', boxShadow: '0 12px 36px rgba(0,0,0,0.5)',
          minWidth: 170, maxHeight: 220, overflowY: 'auto', padding: '4px 0',
        }}>
          {_COUNTRY_CODES.map((c) => (
            <button
              key={c.code}
              type="button"
              onClick={() => { onChange(c.code); setOpen(false); }}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 8,
                padding: '8px 12px', border: 'none',
                background: c.code === value ? 'rgba(67,196,67,0.12)' : 'transparent',
                cursor: 'pointer', fontFamily: 'var(--font-ui)', fontSize: 13.5, color: '#fff', textAlign: 'left',
              }}
              onMouseEnter={(e) => { if (c.code !== value) e.currentTarget.style.background = 'rgba(255,255,255,0.07)'; }}
              onMouseLeave={(e) => { if (c.code !== value) e.currentTarget.style.background = 'transparent'; }}
            >
              <img src={_flagUrl(c.iso)} alt={c.iso} width={22} height={16} style={{ borderRadius: 2, objectFit: 'cover', flexShrink: 0 }} />
              <span style={{ fontWeight: 600 }}>{c.iso}</span>
              <span style={{ color: 'rgba(255,255,255,0.45)' }}>{c.code}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function _FieldError({ msg }) {
  if (!msg) return null;
  return (
    <span style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, fontSize: 12.5, color: 'var(--error, #e53935)', fontFamily: 'var(--font-ui)' }}>
      <Icon name="alert-circle" style={{ width: 13, height: 13, flexShrink: 0 }} />{msg}
    </span>
  );
}

function SpecDownloadModal({ onClose }) {
  const [form, setForm] = React.useState({ name: '', email: '', countryCode: '+91', phone: '', company: '' });
  const [errors, setErrors] = React.useState({});
  const [done, setDone] = React.useState(false);

  React.useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const set = (k) => (ev) => {
    setForm((f) => ({ ...f, [k]: ev.target.value }));
    if (errors[k]) setErrors((e) => ({ ...e, [k]: '' }));
  };
  const setPhone = (ev) => {
    const digits = ev.target.value.replace(/\D/g, '').slice(0, 15);
    setForm((f) => ({ ...f, phone: digits }));
    if (errors.phone) setErrors((e) => ({ ...e, phone: '' }));
  };

  function validate() {
    const e = {};
    if (!form.name.trim())                        e.name  = 'Name is required.';
    if (!form.email.trim())                       e.email = 'Email is required.';
    else if (!_EMAIL_RE.test(form.email.trim()))  e.email = 'Enter a valid email address.';
    if (!form.phone.trim())                       e.phone = 'Phone is required.';
    else if (!_PHONE_RE.test(form.phone.trim()))  e.phone = 'Enter a valid phone number.';
    return e;
  }

  const handleSubmit = (ev) => {
    ev.preventDefault();
    const e = validate();
    if (Object.keys(e).length) { setErrors(e); return; }
    // Validate passed — trigger download
    const a = document.createElement('a');
    a.href = '/broucher/bees_broucher.pdf';
    a.download = 'ADIRATH_BESS_Brochure.pdf';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setDone(true);
  };

  const inputStyle = (key) => ({
    width: '100%', boxSizing: 'border-box', padding: '11px 14px',
    background: 'rgba(255,255,255,0.06)',
    border: `1.5px solid ${errors[key] ? 'var(--error, #e53935)' : 'rgba(255,255,255,0.15)'}`,
    borderRadius: 'var(--radius-lg)', color: '#fff', fontSize: 14.5,
    outline: 'none', fontFamily: 'inherit',
  });
  const onFocus = (ev) => { ev.target.style.borderColor = 'var(--accent)'; ev.target.style.boxShadow = '0 0 0 4px var(--ring)'; };
  const onBlur  = (key) => (ev) => { ev.target.style.borderColor = errors[key] ? 'var(--error, #e53935)' : 'rgba(255,255,255,0.15)'; ev.target.style.boxShadow = 'none'; };

  return (
    <div
      onClick={(ev) => { if (ev.target === ev.currentTarget) onClose(); }}
      style={{ position: 'fixed', inset: 0, background: 'rgba(5,15,31,0.88)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div style={{ background: '#0d1e35', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-2xl)', padding: '36px 32px', width: '100%', maxWidth: 460, position: 'relative', boxShadow: '0 24px 64px rgba(0,0,0,0.6)' }}>

        <button onClick={onClose} style={{ position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', padding: 6, borderRadius: 8, lineHeight: 0 }}>
          <Icon name="x" style={{ width: 19, height: 19 }} />
        </button>

        {!done ? (
          <>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
              <span style={{ width: 46, height: 46, borderRadius: 13, background: 'rgba(67,196,67,0.14)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <Icon name="file-down" style={{ width: 22, height: 22 }} />
              </span>
              <div>
                <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 18, color: '#fff', lineHeight: 1.2 }}>Download BESS Brochure</div>
                <div style={{ fontSize: 12.5, color: 'rgba(255,255,255,0.45)', marginTop: 3 }}>Full spec sheet &amp; product details</div>
              </div>
            </div>
            <div style={{ height: 1, background: 'rgba(255,255,255,0.08)', marginBottom: 22 }} />

            <form onSubmit={handleSubmit} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>

              {/* Name */}
              <div>
                <label style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 7 }}>
                  Full Name <span style={{ color: 'var(--error, #e53935)' }}>*</span>
                </label>
                <input type="text" value={form.name} placeholder="Your name" onChange={set('name')}
                  style={inputStyle('name')} onFocus={onFocus} onBlur={onBlur('name')} />
                <_FieldError msg={errors.name} />
              </div>

              {/* Email */}
              <div>
                <label style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 7 }}>
                  Work Email <span style={{ color: 'var(--error, #e53935)' }}>*</span>
                </label>
                <input type="email" value={form.email} placeholder="you@company.com" onChange={set('email')}
                  style={inputStyle('email')} onFocus={onFocus} onBlur={onBlur('email')} />
                <_FieldError msg={errors.email} />
              </div>

              {/* Phone — country code picker + digits-only input */}
              <div>
                <label style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 7 }}>
                  Phone Number <span style={{ color: 'var(--error, #e53935)' }}>*</span>
                </label>
                <div style={{ display: 'flex', gap: 8 }}>
                  <_CountryDropdown
                    value={form.countryCode}
                    onChange={(code) => setForm((f) => ({ ...f, countryCode: code }))}
                    hasError={!!errors.phone}
                  />
                  <input type="tel" value={form.phone} placeholder="9876543210" onChange={setPhone} maxLength={15}
                    style={{ ...inputStyle('phone'), flex: 1 }} onFocus={onFocus} onBlur={onBlur('phone')} />
                </div>
                <_FieldError msg={errors.phone} />
              </div>

              {/* Company — optional */}
              <div>
                <label style={{ display: 'block', fontFamily: 'var(--font-ui)', fontSize: 13, fontWeight: 600, color: 'rgba(255,255,255,0.7)', marginBottom: 7 }}>
                  Company <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.35)', fontWeight: 400 }}>(optional)</span>
                </label>
                <input type="text" value={form.company} placeholder="Organisation name" onChange={set('company')}
                  style={inputStyle('company')} onFocus={onFocus} onBlur={onBlur('company')} />
              </div>

              {/* Field-level error banner */}
              {Object.keys(errors).some((k) => errors[k]) && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderRadius: 'var(--radius-md)', background: 'rgba(229,57,53,0.08)', border: '1px solid rgba(229,57,53,0.25)', color: '#c62828', fontSize: 13.5 }}>
                  <Icon name="alert-triangle" style={{ width: 18, height: 18, flexShrink: 0 }} />
                  Please fix the errors above before submitting.
                </div>
              )}

              <button type="submit" style={{ marginTop: 4, width: '100%', padding: '13px 0', background: 'var(--energy)', color: 'var(--blue-900)', border: 'none', borderRadius: 'var(--radius-lg)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 9 }}>
                <Icon name="download" style={{ width: 17, height: 17 }} /> Download Brochure
              </button>
              <p style={{ fontSize: 11.5, color: 'rgba(255,255,255,0.28)', textAlign: 'center', margin: 0, lineHeight: 1.6 }}>
                Your details are used solely to process this request and will not be shared with third parties.
              </p>
            </form>
          </>
        ) : (
          <div style={{ textAlign: 'center', padding: '12px 0' }}>
            <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(67,196,67,0.14)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
              <Icon name="check-circle" style={{ width: 32, height: 32 }} />
            </div>
            <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 20, color: '#fff', marginBottom: 8 }}>Download started</div>
            <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.55)', marginBottom: 28, lineHeight: 1.65 }}>
              Your BESS brochure is downloading.<br />Our team may reach out to discuss your requirements.
            </div>
            <button onClick={onClose} style={{ padding: '11px 32px', background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.14)', borderRadius: 'var(--radius-lg)', color: '#fff', fontFamily: 'var(--font-ui)', fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function HighlightText({ text, accentColor }) {
  if (!text) return null;
  const parts = text.split(/(\*\*[^*]+\*\*)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith('**') && part.endsWith('**')
          ? <strong key={i} style={{ color: accentColor, fontWeight: 700 }}>{part.slice(2, -2)}</strong>
          : <React.Fragment key={i}>{part}</React.Fragment>
      )}
    </>
  );
}

function DeliverCard({ title, sub, icon, dark = true }) {
  const [h, setH] = React.useState(false);
  const cardBg  = dark
    ? (h ? 'rgba(67,196,67,0.07)' : 'var(--glass-dark-bg)')
    : (h ? 'rgba(67,196,67,0.05)' : 'var(--surface-card)');
  const cardBdr = dark
    ? (h ? 'rgba(67,196,67,0.35)' : 'var(--glass-dark-border)')
    : (h ? 'rgba(67,196,67,0.35)' : 'var(--border-subtle)');
  const iconBg  = dark ? 'rgba(67,196,67,0.13)' : 'var(--accent-soft)';
  const iconBdr = dark ? '1px solid rgba(67,196,67,0.25)' : '1px solid var(--border-subtle)';
  const iconClr = dark ? 'var(--energy-bright)' : 'var(--accent)';
  const titleC  = dark ? '#fff' : 'var(--text-strong)';
  const subC    = dark ? 'rgba(255,255,255,0.5)' : 'var(--text-muted)';
  return (
    <div
      onMouseEnter={() => setH(true)}
      onMouseLeave={() => setH(false)}
      style={{
        height: '100%', borderRadius: 'var(--radius-xl)',
        background: cardBg, border: `1px solid ${cardBdr}`,
        padding: '28px 24px', display: 'flex', flexDirection: 'column', gap: 16,
        transform: h ? 'translateY(-5px)' : 'none',
        boxShadow: h ? `0 16px 44px rgba(67,196,67,${dark ? '0.12' : '0.08'})` : (dark ? 'none' : 'var(--shadow-sm)'),
        transition: 'all var(--dur-med) var(--ease-out)',
      }}
    >
      <span style={{ width: 52, height: 52, borderRadius: 14, background: iconBg, border: iconBdr, color: iconClr, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, transition: 'background var(--dur-fast)' }}>
        <Icon name={icon} style={{ width: 24, height: 24 }} />
      </span>
      <div style={{ flex: 1 }}>
        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16, color: titleC, marginBottom: sub ? 7 : 0, lineHeight: 1.3 }}>{title}</div>
        {sub && <div style={{ fontSize: 13.5, color: subC, lineHeight: 1.55 }}>{sub}</div>}
      </div>
      <div style={{ height: 2, borderRadius: 2, background: `linear-gradient(90deg, ${h ? 'var(--energy)' : (dark ? 'rgba(67,196,67,0.3)' : 'var(--accent-soft)')}, transparent)`, width: h ? '80%' : '50%', transition: 'width var(--dur-med), background var(--dur-med)' }} />
    </div>
  );
}

export function ServiceDetail({ serviceKey, onNavigate }) {
  const s = SERVICE_CATALOG[serviceKey];
  if (!s) return null;
  const idx = SERVICE_ORDER.indexOf(serviceKey);
  const next = SERVICE_CATALOG[SERVICE_ORDER[(idx + 1) % SERVICE_ORDER.length]];
  const nextKey = SERVICE_ORDER[(idx + 1) % SERVICE_ORDER.length];

  const [showDownload, setShowDownload] = React.useState(false);
  const faqs = [
    [`Do you handle ${s.short} projects end-to-end?`, 'Yes — ADIRATH covers design, engineering, manufacturing, testing, commissioning and lifetime support in-house.'],
    ['What certifications apply?', 'Our systems are built to applicable IEC, ISO, AIS and sector-specific standards, with full documentation and traceability.'],
    ['Can solutions be customised?', 'Every deployment is engineered to your requirement — capacity, form factor, integration and compliance.'],
  ];

  return (
    <div>
      <section data-screen-label={s.name} style={{ position: 'relative', overflow: 'hidden', background: '#050f1f', color: '#fff', padding: 'calc(var(--space-9) + 48px) 0 var(--space-9)' }}>
        <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("${IMG(s.hero)}")`, backgroundSize: 'cover', backgroundPosition: 'center', opacity: 0.45 }} />
        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(90deg, rgba(5,15,31,0.9) 0%, rgba(5,15,31,0.6) 55%, rgba(5,15,31,0.25) 100%)' }} />
        <div style={{ position: 'relative', maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
          <nav style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 22, fontSize: 15, color: 'rgba(255,255,255,0.6)' }}>
            <a onClick={() => onNavigate('Home')} style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>Home</a>
            <Icon name="chevron-right" style={{ width: 13, height: 13 }} />
            <a onClick={() => onNavigate('Services')} style={{ color: 'rgba(255,255,255,0.6)', cursor: 'pointer' }}>Services</a>
            <Icon name="chevron-right" style={{ width: 13, height: 13 }} />
            <span style={{ color: 'var(--energy-bright)' }}>{s.short}</span>
          </nav>
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, width: 60, height: 60, borderRadius: 16, background: 'var(--energy)', color: 'var(--blue-900)', justifyContent: 'center', marginBottom: 22 }}>
            <Icon name={s.icon} style={{ width: 30, height: 30 }} />
          </span>
          <h1 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'var(--fs-display-2)', letterSpacing: '-0.03em', lineHeight: 1.05, margin: '0 0 18px', maxWidth: 820, color: '#fff' }}>{s.name}</h1>
          <p style={{ fontSize: 'var(--fs-lead)', color: 'rgba(255,255,255,0.82)', maxWidth: 640, margin: '0 0 30px' }}>{s.intro}</p>
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
            <Button variant="energy" size="lg" iconRight={<Icon name="arrow-right" style={{ width: 18, height: 18 }} />} onClick={() => onNavigate('Contact')}>Request Consultation</Button>
            <Button variant="glass" size="lg" onClick={() => onNavigate('Services')}>All Services</Button>
            {serviceKey === 'bess' && (
              <Button variant="glass" size="lg" iconRight={<Icon name="download" style={{ width: 16, height: 16 }} />} onClick={() => setShowDownload(true)}>Download Brochure</Button>
            )}
          </div>
          {s.highlights && (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginTop: 24 }}>
              {s.highlights.map((hl) => (
                <span key={hl} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '8px 16px', borderRadius: 'var(--radius-pill)', background: 'rgba(67,196,67,0.12)', border: '1px solid rgba(67,196,67,0.28)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 14, letterSpacing: '0.04em', color: 'var(--energy-bright)' }}>
                  <Icon name="check-circle" style={{ width: 15, height: 15 }} /> {hl}
                </span>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── NPD Intro Section ────────────────────────────────────────────── */}
      {s.introSection && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <div className="adr-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'clamp(32px,5vw,72px)', alignItems: 'center' }}>
              <div>
                <SectionHead eyebrow="R&D" title={s.introSection.title} max={560} />
                <p style={{ fontSize: 15, color: 'var(--text-body)', lineHeight: 1.8, margin: '16px 0 0', maxWidth: 560 }}>{s.introSection.body}</p>
              </div>
              {s.introSection.img && (
                <div style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', aspectRatio: '4/3' }}>
                  <img src={s.introSection.img} alt={s.introSection.title} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* ── NPD Approach Section ─────────────────────────────────────────── */}
      {s.approachSection && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead dark eyebrow="Approach" title={s.approachSection.title} sub={s.approachSection.sub} max={600} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 24, marginTop: 40 }}>
              {s.approachSection.items.map((item) => (
                <Reveal key={item.title} variant="up">
                  <div style={{ background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-lg)', padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: 'rgba(67,196,67,0.12)', border: '1px solid rgba(67,196,67,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={item.icon} style={{ width: 19, height: 19, color: 'var(--energy-bright)' }} />
                      </div>
                      <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: '#fff', margin: 0 }}>{item.title}</h4>
                    </div>
                    <p style={{ fontSize: 14, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── NPD Focus Areas Section ──────────────────────────────────────── */}
      {s.focusAreasSection && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead eyebrow="Focus Areas" title={s.focusAreasSection.title} sub={s.focusAreasSection.sub} max={640} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 40 }}>
              {s.focusAreasSection.items.map((item, idx) => (
                <Reveal key={item.title} variant="up" delay={idx * 0.05}>
                  <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: 'var(--accent-soft)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={item.icon} style={{ width: 19, height: 19, color: 'var(--accent)' }} />
                      </div>
                      <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--text-strong)', margin: 0, lineHeight: 1.3 }}>{item.title}</h4>
                    </div>
                    <div style={{ width: '100%', height: 1, background: 'var(--border-subtle)' }} />
                    <p style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── NPD Innovations Section ──────────────────────────────────────── */}
      {s.innovationsSection && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead dark eyebrow="Innovations" title={s.innovationsSection.title} sub={s.innovationsSection.sub} max={620} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 40 }}>
              {s.innovationsSection.items.map((item, idx) => (
                <Reveal key={item.title} variant="up" delay={idx * 0.05}>
                  <div style={{ background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-lg)', padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: 'rgba(67,196,67,0.12)', border: '1px solid rgba(67,196,67,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={item.icon} style={{ width: 19, height: 19, color: 'var(--energy-bright)' }} />
                      </div>
                      <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: '#fff', margin: 0, lineHeight: 1.3 }}>{item.title}</h4>
                    </div>
                    <div style={{ width: '100%', height: 1, background: 'rgba(255,255,255,0.06)' }} />
                    <p style={{ fontSize: 13.5, color: 'rgba(255,255,255,0.72)', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── NPD Facilities Section ───────────────────────────────────────── */}
      {s.facilitiesSection && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead eyebrow="Facilities" title={s.facilitiesSection.title} sub={s.facilitiesSection.sub} max={680} />
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 20, marginTop: 40 }}>
              {s.facilitiesSection.items.map((item, idx) => (
                <Reveal key={item.title} variant="up" delay={idx * 0.05}>
                  <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 14, height: '100%' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: 'var(--accent-soft)', border: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                        <Icon name={item.icon} style={{ width: 19, height: 19, color: 'var(--accent)' }} />
                      </div>
                      <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: 'var(--text-strong)', margin: 0, lineHeight: 1.3 }}>{item.title}</h4>
                    </div>
                    <div style={{ width: '100%', height: 1, background: 'var(--border-subtle)' }} />
                    <p style={{ fontSize: 13.5, color: 'var(--text-body)', lineHeight: 1.7, margin: 0 }}>{item.desc}</p>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── NPD Showcase — right after hero ──────────────────────────────── */}
      {s.npdShowcase && (() => {
        const ns = s.npdShowcase;
        return (
          <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
            <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
              {/* Tagline + tags */}
              <div style={{ textAlign: 'center', marginBottom: 44 }}>
                <SectionHead dark align="center" eyebrow="NPD" title={ns.tagline} max={700} />
                <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: 10, marginTop: 18 }}>
                  {ns.tags.map((t) => (
                    <span key={t} style={{ padding: '6px 16px', borderRadius: 'var(--radius-pill)', background: 'rgba(67,196,67,0.1)', border: '1px solid rgba(67,196,67,0.22)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12.5, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--energy-bright)' }}>{t}</span>
                  ))}
                </div>
              </div>

              {/* 6 Solution cards — 3×2 grid */}
              <div className="adr-npd-solutions" style={{ display: 'grid', gap: 18 }}>
                {ns.solutions.map(({ icon, title, grad }, si) => (
                  <Reveal key={title} variant="up" delay={si * 0.06}>
                    <div style={{ position: 'relative', height: 190, borderRadius: 'var(--radius-xl)', overflow: 'hidden', background: grad, cursor: 'default' }}>
                      <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.05) 0%, rgba(0,0,0,0.65) 100%)' }} />
                      {/* icon top-right */}
                      <span style={{ position: 'absolute', top: 14, right: 14, width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.12)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name={icon} style={{ width: 18, height: 18 }} />
                      </span>
                      {/* title bottom */}
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '14px 18px' }}>
                        <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: 12.5, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', lineHeight: 1.3 }}>{title}</div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              {/* Process steps */}
              <Reveal variant="up" delay={0.1} style={{ marginTop: 36 }}>
                <div className="adr-npd-process" style={{ background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-xl)', padding: '28px 32px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  {ns.process.map(({ icon, label }, pi) => (
                    <React.Fragment key={label}>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 }}>
                        <span style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(67,196,67,0.12)', border: '1px solid rgba(67,196,67,0.25)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name={icon} style={{ width: 24, height: 24 }} />
                        </span>
                        <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12.5, letterSpacing: '0.1em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.85)' }}>{label}</span>
                      </div>
                      {pi < ns.process.length - 1 && (
                        <div className="adr-npd-connector" style={{ flex: 1, height: 1, background: 'linear-gradient(90deg, rgba(67,196,67,0.35), rgba(67,196,67,0.1))', margin: '0 16px', marginBottom: 22 }} />
                      )}
                    </React.Fragment>
                  ))}
                </div>
              </Reveal>

              {/* Capabilities */}
              <Reveal variant="up" delay={0.15} style={{ marginTop: 18 }}>
                <div style={{ background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-xl)', padding: '24px 28px' }}>
                  <div className="adr-npd-caps" style={{ display: 'grid', gap: 12 }}>
                    {ns.capabilities.map((cap, ci) => (
                      <div key={cap} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, padding: '14px 10px', borderRadius: 12, background: ci < 5 ? 'rgba(67,196,67,0.07)' : 'rgba(255,255,255,0.04)', border: '1px solid var(--glass-dark-border)', textAlign: 'center' }}>
                        <span style={{ width: 36, height: 36, borderRadius: 9, background: ci < 5 ? 'rgba(67,196,67,0.15)' : 'rgba(255,255,255,0.08)', color: ci < 5 ? 'var(--energy-bright)' : 'rgba(255,255,255,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name={['battery-charging','cog','battery','cpu','rocket','settings-2','pen-tool','zap','wifi','factory'][ci]} style={{ width: 17, height: 17 }} />
                        </span>
                        <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 11.5, letterSpacing: '0.07em', textTransform: 'uppercase', color: ci < 5 ? 'rgba(255,255,255,0.9)' : 'rgba(255,255,255,0.65)', lineHeight: 1.3 }}>{cap}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            </div>
          </section>
        );
      })()}

      {/* ── Mobility Showcase Bento — dark, right after hero ─────────────── */}
      {s.specCategories && (() => {
        const cardGrads = [
          'linear-gradient(145deg, #0891b2 0%, #0c4a6e 100%)',
          'linear-gradient(145deg, #4f46e5 0%, #1e1b4b 100%)',
          'linear-gradient(145deg, #d97706 0%, #7c2d12 100%)',
        ];
        return (
          <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
            <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
              <SectionHead dark eyebrow="Battery Systems" title="Purpose-Built for Every Mobility Application." max={640} />
              {/* Top 3 image cards */}
              <div className="adr-mobility-cards" style={{ display: 'grid', gap: 22, marginTop: 40 }}>
                {s.specCategories.slice(0, 3).map(({ icon, title, bullets, spec }, ci) => (
                  <Reveal key={title} variant="up" delay={ci * 0.08}>
                    <div style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', height: '100%', display: 'flex', flexDirection: 'column', border: '1px solid var(--glass-dark-border)' }}>
                      {/* Image / gradient area */}
                      <div style={{ position: 'relative', height: 200, background: cardGrads[ci], flexShrink: 0 }}>
                        {/* dim overlay */}
                        <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, rgba(0,0,0,0.15) 0%, rgba(0,0,0,0.55) 100%)' }} />
                        {/* icon badge top-right */}
                        <span style={{ position: 'absolute', top: 16, right: 16, width: 40, height: 40, borderRadius: 11, background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(6px)', WebkitBackdropFilter: 'blur(6px)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <Icon name={icon} style={{ width: 20, height: 20 }} />
                        </span>
                        {/* title overlay bottom */}
                        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, padding: '16px 20px' }}>
                          <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 800, fontSize: 13, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#fff', lineHeight: 1.3 }}>{title}</div>
                        </div>
                      </div>
                      {/* Content area */}
                      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: 'var(--glass-dark-bg)', padding: '22px 22px 20px' }}>
                        <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                          {bullets.map((b, bi) => (
                            <li key={bi} style={{ display: 'flex', gap: 9, alignItems: 'flex-start', fontSize: 14, color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
                              <span style={{ flexShrink: 0, width: 18, height: 18, borderRadius: '50%', background: 'rgba(67,196,67,0.15)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                                <Icon name="check" style={{ width: 10, height: 10 }} />
                              </span>
                              {b}
                            </li>
                          ))}
                        </ul>
                        {/* Spec strip */}
                        <div style={{ marginTop: 18, padding: '12px 16px', borderRadius: 10, background: 'rgba(67,196,67,0.08)', border: '1px solid rgba(67,196,67,0.2)', fontFamily: 'var(--font-mono)', fontSize: 12.5, fontWeight: 700, color: 'var(--energy-bright)', lineHeight: 1.6 }}>
                          {spec}
                        </div>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
              {/* Marine — full-width bottom card */}
              {s.specCategories[3] && (
                <Reveal variant="up" delay={0.25} style={{ marginTop: 22 }}>
                  <div className="adr-marine-split" style={{ borderRadius: 'var(--radius-xl)', overflow: 'hidden', border: '1px solid var(--glass-dark-border)', display: 'grid', alignItems: 'stretch' }}>
                    {/* Left: deep ocean gradient "image" */}
                    <div style={{ position: 'relative', minHeight: 260, background: 'linear-gradient(145deg, #0369a1 0%, #0c1445 100%)' }}>
                      <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse at 30% 60%, rgba(14,165,233,0.3) 0%, transparent 65%)' }} />
                      <div style={{ position: 'relative', padding: '36px 32px', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', gap: 8 }}>
                        <span style={{ width: 52, height: 52, borderRadius: 14, background: 'rgba(255,255,255,0.12)', border: '1px solid rgba(255,255,255,0.18)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 8 }}>
                          <Icon name={s.specCategories[3].icon} style={{ width: 26, height: 26 }} />
                        </span>
                        <div style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.4rem, 2.4vw, 2rem)', color: '#fff', letterSpacing: '-0.02em', lineHeight: 1.1 }}>{s.specCategories[3].title}</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontSize: 11.5, fontWeight: 700, color: 'rgba(255,255,255,0.55)', letterSpacing: '0.1em', textTransform: 'uppercase', marginTop: 6 }}>System Range</div>
                        <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: 'var(--energy-bright)', fontSize: 13, lineHeight: 1.55 }}>{s.specCategories[3].spec}</div>
                      </div>
                    </div>
                    {/* Right: bullets */}
                    <div style={{ background: 'var(--glass-dark-bg)', padding: '36px 40px', display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 20 }}>
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {s.specCategories[3].bullets.map((b, bi) => (
                          <li key={bi} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 15, color: 'rgba(255,255,255,0.82)', lineHeight: 1.65 }}>
                            <span style={{ flexShrink: 0, width: 22, height: 22, borderRadius: '50%', background: 'rgba(67,196,67,0.15)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                              <Icon name="check" style={{ width: 12, height: 12 }} />
                            </span>
                            {b}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </Reveal>
              )}
            </div>
          </section>
        );
      })()}

      {/* Solution Architecture overview */}
      {s.architecture && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead dark align="center" eyebrow="Solution Architecture" title="One Architecture, Three Deployment Modes." max={680} />
            {/* Segmented scale bar — electric/power themed */}
            <div style={{ margin: '36px 0 52px', background: 'var(--glass-dark-bg)', borderRadius: 'var(--radius-xl)', padding: '28px 28px 22px', boxShadow: '0 12px 40px rgba(26,128,168,0.1), 0 12px 40px rgba(196,126,24,0.06)' }}>
              {/* Glowing power bar */}
              <div style={{ display: 'flex', gap: 4, height: 8, marginBottom: 6 }}>
                <div style={{ flex: 1, background: 'linear-gradient(90deg, #126898, #2bbce8)', borderRadius: '6px 0 0 6px', boxShadow: '0 0 18px #1A80A855, 0 2px 10px #1A80A838' }} />
                <div style={{ flex: 1.4, background: 'linear-gradient(90deg, #157840, #2cd870)', boxShadow: '0 0 18px #1A915055, 0 2px 10px #1A915038' }} />
                <div style={{ flex: 1.8, background: 'linear-gradient(90deg, #a86212, #f0a424)', borderRadius: '0 6px 6px 0', boxShadow: '0 0 18px #C47E1855, 0 2px 10px #C47E1838' }} />
              </div>
              {/* Segment boundary ticks */}
              <div style={{ position: 'relative', height: 10, marginBottom: 14 }}>
                <div style={{ position: 'absolute', left: `calc(${100/4.2}% + 2px)`, top: 0, width: 1, height: 8, background: 'rgba(255,255,255,0.2)' }} />
                <div style={{ position: 'absolute', left: `calc(${100*2.4/4.2}% + 2px)`, top: 0, width: 1, height: 8, background: 'rgba(255,255,255,0.2)' }} />
              </div>
              {/* Labels */}
              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                {[{ tag: 'LV', range: 'Up to 50 kWh', accent: '#1A80A8' }, { tag: 'MV', range: '50–600 kWh', accent: '#1A9150' }, { tag: 'HV', range: '600 kWh – 5 MWh+', accent: '#C47E18' }].map(({ tag, range, accent }) => (
                  <div key={tag} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '1rem', color: accent, letterSpacing: '0.06em' }}>
                      <Icon name="zap" style={{ width: 12, height: 12 }} />{tag}
                    </span>
                    <span style={{ fontFamily: 'var(--font-ui)', fontSize: 13.5, color: 'rgba(255,255,255,0.6)' }}>{range}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Mode cards */}
            <div style={{ display: 'grid', gap: 22 }} className="adr-bento">
              {s.architecture.arcModes.map((m, i) => (
                <Reveal key={m.label} variant="up" delay={i * 0.07}>
                  <div style={{ position: 'relative', background: m.img ? `linear-gradient(rgba(5,15,31,0.82),rgba(5,15,31,0.82)), url('${m.img.replace(/ /g,'%20')}') center/cover` : 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderTop: `3px solid ${m.accent}`, borderRadius: 'var(--radius-xl)', padding: '28px 26px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
                    <span style={{ position: 'absolute', right: 18, bottom: 14, fontFamily: 'var(--font-display)', fontWeight: 900, fontSize: '6.5rem', color: m.accent, opacity: 0.07, letterSpacing: '-0.05em', lineHeight: 1, userSelect: 'none', pointerEvents: 'none' }}>{m.tag}</span>
                    <span style={{ display: 'inline-flex', width: 48, height: 48, borderRadius: 14, background: `${m.accent}20`, color: m.accent, alignItems: 'center', justifyContent: 'center', border: `1px solid ${m.accent}40`, marginBottom: 22, flexShrink: 0 }}>
                      <Icon name={m.icon} style={{ width: 22, height: 22 }} />
                    </span>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                      <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 800, fontSize: '1.05rem', color: m.accent, letterSpacing: '0.06em' }}>{m.tag}</span>
                      <span style={{ width: 1, height: 12, background: 'rgba(255,255,255,0.18)', flexShrink: 0 }} />
                      <span style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 12.5, letterSpacing: '0.13em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.55)' }}>{m.label}</span>
                    </div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontSize: 15.5, fontWeight: 700, color: '#fff', paddingBottom: 16, marginBottom: 16, borderBottom: '1px solid var(--border-on-dark)' }}>{m.footprint}</div>
                    <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 10, flex: 1 }}>
                      {m.points.map((p, pi) => (
                        <li key={pi} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 14.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.6 }}>
                          <Icon name="check" style={{ width: 16, height: 16, color: m.accent, flexShrink: 0, marginTop: 2 }} />
                          {p}
                        </li>
                      ))}
                    </ul>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Showcase Modes — full-bleed image split (mobility & similar) ── */}
      {s.modes && s.showcaseModes && s.modes.map((m, i) => {
        const dark = i % 2 !== 0;
        const imgRight = i % 2 === 0;
        const bg  = dark ? '#050f1f'                : 'var(--surface-page)';
        const titleC  = dark ? '#fff'               : 'var(--text-strong)';
        const bodyC   = dark ? 'rgba(255,255,255,0.72)' : 'var(--text-body)';
        const labelC  = dark ? 'var(--energy-bright)'   : 'var(--accent)';
        const pillBg  = dark ? 'rgba(67,196,67,0.06)'   : 'var(--accent-soft)';
        const pillBdr = dark ? 'rgba(67,196,67,0.16)'   : 'var(--border-subtle)';
        const pillTxt = dark ? 'rgba(255,255,255,0.82)' : 'var(--text-strong)';
        const checkC  = dark ? 'var(--energy-bright)'   : 'var(--accent)';
        const overlayGrad = (toLeft) =>
          dark
            ? toLeft
              ? 'linear-gradient(270deg,rgba(5,15,31,0.85) 0%,rgba(5,15,31,0.35) 55%,rgba(5,15,31,0.1) 100%)'
              : 'linear-gradient(90deg,rgba(5,15,31,0.85) 0%,rgba(5,15,31,0.35) 55%,rgba(5,15,31,0.1) 100%)'
            : toLeft
              ? 'linear-gradient(270deg,rgba(240,244,248,0.92) 0%,rgba(240,244,248,0.5) 55%,transparent 100%)'
              : 'linear-gradient(90deg,rgba(240,244,248,0.92) 0%,rgba(240,244,248,0.5) 55%,transparent 100%)';

        return (
          <React.Fragment key={m.tag}>
          <section style={{ background: bg, overflow: 'hidden' }}>
            <div className="adr-showcase-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', minHeight: 560 }}>

              {/* Image panel */}
              <div style={{ order: imgRight ? 1 : 0, position: 'relative', overflow: 'hidden', minHeight: 320 }}>
                <div style={{ position: 'absolute', inset: 0, backgroundImage: `url("${m.img}")`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                <div style={{ position: 'absolute', inset: 0, background: overlayGrad(imgRight) }} />
                <div style={{ position: 'absolute', bottom: 28, left: 28 }}>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 9, padding: '10px 18px', background: 'rgba(5,15,31,0.78)', backdropFilter: 'blur(12px)', WebkitBackdropFilter: 'blur(12px)', border: '1px solid rgba(67,196,67,0.35)', borderRadius: 'var(--radius-pill)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 13.5, color: 'var(--energy-bright)' }}>
                    <Icon name={m.icon} style={{ width: 15, height: 15 }} /> {m.tag}
                  </span>
                </div>
              </div>

              {/* Content panel */}
              <div className="adr-showcase-content" style={{ order: imgRight ? 0 : 1, padding: 'clamp(40px,5vw,72px) clamp(32px,5vw,60px)', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 11.5, letterSpacing: '0.16em', textTransform: 'uppercase', color: labelC, marginBottom: 14 }}>{m.tag}</div>
                <h2 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.45rem,2vw,2.1rem)', color: titleC, margin: '0 0 18px', lineHeight: 1.15 }}>{m.label}</h2>
                <p style={{ fontSize: 15, color: bodyC, lineHeight: 1.8, margin: '0 0 12px' }}>{m.sub}</p>
                {m.variants && <p style={{ fontSize: 15, color: bodyC, lineHeight: 1.8, margin: '0 0 28px' }}>{m.variants}</p>}
                {m.focusAreas && (
                  <>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: titleC, marginBottom: 10, marginTop: 4 }}>{m.focusAreasLabel || 'Our Focus Areas'}</div>
                    <ul style={{ margin: '0 0 20px', padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 6 }}>
                      {m.focusAreas.map((f) => (
                        <li key={f} style={{ fontSize: 14.5, lineHeight: 1.6, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                          <span style={{ color: checkC, marginTop: 3, flexShrink: 0 }}>•</span>
                          <span style={{ color: checkC }}><HighlightText text={f} accentColor={titleC} /></span>
                        </li>
                      ))}
                    </ul>
                  </>
                )}
                {m.vision && (
                  <>
                    <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 15, color: titleC, marginBottom: 8 }}>Our Vision</div>
                    <p style={{ fontSize: 15, color: bodyC, lineHeight: 1.8, margin: 0 }}>
                      <HighlightText text={m.vision} accentColor={checkC} />
                    </p>
                  </>
                )}
                {m.features && (
                  <>
                    <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 11.5, letterSpacing: '0.14em', textTransform: 'uppercase', color: labelC, marginBottom: 14 }}>{m.featuresLabel || 'Applications'}</div>
                    <div className="adr-showcase-apps" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 9 }}>
                      {m.features.map((f) => (
                        <div key={f} style={{ display: 'flex', alignItems: 'flex-start', gap: 9, padding: '10px 13px', background: pillBg, border: `1px solid ${pillBdr}`, borderRadius: 'var(--radius-md)', fontSize: 13.5, color: pillTxt, lineHeight: 1.4 }}>
                          <Icon name="check" style={{ width: 13, height: 13, color: checkC, flexShrink: 0, marginTop: 1 }} />
                          {f}
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>

            </div>
          </section>
          {m.subCards && (
            <section style={{ background: dark ? 'var(--surface-inverse)' : 'var(--surface-page)', padding: 'clamp(40px,5vw,64px) 0' }}>
              <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
                {m.subCardsTitle && (
                  <div style={{ marginBottom: 36 }}>
                    <h3 style={{ fontFamily: 'var(--font-display)', fontWeight: 800, fontSize: 'clamp(1.3rem,2vw,1.75rem)', color: titleC, margin: 0 }}>{m.subCardsTitle}</h3>
                    <div style={{ width: 48, height: 3, background: checkC, borderRadius: 2, marginTop: 12 }} />
                  </div>
                )}
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 20 }}>
                  {m.subCards.map((card) => (
                    <div key={card.title} style={{ background: dark ? 'var(--glass-dark-bg)' : 'var(--surface-card)', border: `1px solid ${dark ? 'var(--glass-dark-border)' : 'var(--border-subtle)'}`, borderRadius: 'var(--radius-lg)', padding: '24px 22px', display: 'flex', flexDirection: 'column', gap: 16 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 42, height: 42, borderRadius: 'var(--radius-md)', background: dark ? 'rgba(67,196,67,0.12)' : 'var(--accent-soft)', border: `1px solid ${dark ? 'rgba(67,196,67,0.25)' : 'var(--border-subtle)'}`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                          <Icon name={card.icon} style={{ width: 19, height: 19, color: checkC }} />
                        </div>
                        <h4 style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 16, color: titleC, margin: 0, lineHeight: 1.2 }}>{card.title}</h4>
                      </div>
                      <div style={{ width: '100%', height: 1, background: dark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)' }} />
                      <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {card.items.map((item) => (
                          <li key={item} style={{ fontSize: 13.5, color: bodyC, lineHeight: 1.6, display: 'flex', alignItems: 'flex-start', gap: 8 }}>
                            <span style={{ color: checkC, flexShrink: 0, fontSize: 16, lineHeight: 1.4 }}>•</span>
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            </section>
          )}
        </React.Fragment>
        );
      })}

      {/* Individual deployment mode sections — light / dark / light, image alternates sides */}
      {s.modes && !s.showcaseModes && s.modes.map((m, i) => {
        // If total mode count is even, flip the parity so the last mode is always light
        // (Why ADIRATH section below is always dark — this avoids same-color adjacency)
        const dark = s.modes.length % 2 === 0 ? i % 2 === 0 : i % 2 !== 0;
        const imgLeft = i % 2 === 0;   // image on left for even, right for odd
        const imgSrc = m.img ? m.img.replace(/ /g, '%20') : null;

        const imagePanel = imgSrc && (
          <Reveal key="img" variant="up" style={{ height: '100%' }}>
            <div style={{
              borderRadius: 'var(--radius-2xl)', overflow: 'hidden', position: 'relative',
              boxShadow: 'var(--shadow-xl)', height: '100%', minHeight: 320,
              backgroundImage: `url("${imgSrc}")`,
              backgroundSize: m.imgFit || 'cover',
              backgroundPosition: 'center',
              backgroundRepeat: 'no-repeat',
              backgroundColor: m.imgFit === 'contain' ? (dark ? '#050f1f' : 'var(--surface-sunken)') : undefined,
            }}>
              <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(180deg, transparent 55%, rgba(5,15,31,0.55))' }} />
              <div style={{ position: 'absolute', bottom: 22, left: 22, right: 22 }}>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, padding: '8px 16px', borderRadius: 'var(--radius-pill)', background: 'rgba(5,15,31,0.72)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.12)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 14, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--energy-bright)' }}>
                  <Icon name={m.icon} style={{ width: 16, height: 16 }} />{m.tag}
                </span>
              </div>
            </div>
          </Reveal>
        );

        const contentPanel = (
          <div key="content">
            <SectionHead dark={dark} eyebrow={m.tag} title={m.label} />
            {m.sub && <p style={{ margin: '18px 0 0', fontSize: 15.5, color: dark ? 'rgba(255,255,255,0.78)' : 'var(--text-body)', lineHeight: 1.8 }}>{m.sub}</p>}
            {m.variants && <p style={{ margin: '16px 0 0', fontSize: 15.5, color: dark ? 'rgba(255,255,255,0.78)' : 'var(--text-body)', lineHeight: 1.8 }}>{m.variants}</p>}
            {m.spec && <div style={{ fontFamily: 'var(--font-mono)', fontSize: 14, color: dark ? 'var(--energy-bright)' : 'var(--accent)', margin: '18px 0 5px' }}>{m.spec}</div>}
            {m.specList && (
              <div className="adr-spec-grid" style={{ display: 'grid', gap: 10, marginTop: 24, marginBottom: 28 }}>
                {m.specList.map(([k, v]) => (
                  <div key={k} style={{ background: dark ? 'rgba(255,255,255,0.05)' : 'var(--surface-card)', border: dark ? '1px solid var(--glass-dark-border)' : '1px solid var(--border-subtle)', borderRadius: 'var(--radius-lg)', padding: '11px 14px' }}>
                    <div style={{ fontSize: 12, color: dark ? 'rgba(255,255,255,0.55)' : 'var(--text-muted)', fontFamily: 'var(--font-ui)', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 6 }}>{k}</div>
                    <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 15, color: dark ? 'var(--energy-bright)' : 'var(--accent)' }}>{v}</div>
                  </div>
                ))}
              </div>
            )}
            {m.features && m.features.length > 0 && (
              <ul style={{ margin: 0, padding: 0, listStyle: 'none', display: 'flex', flexDirection: 'column', gap: 13 }}>
                {m.features.map((f, fi) => (
                  <li key={fi} style={{ display: 'flex', gap: 12, alignItems: 'flex-start', fontSize: 15.5, color: dark ? 'rgba(255,255,255,0.85)' : 'var(--text-body)', lineHeight: 1.7 }}>
                    <span style={{ flexShrink: 0, width: 23, height: 23, borderRadius: '50%', background: dark ? 'rgba(67,196,67,0.15)' : 'var(--accent-soft)', color: dark ? 'var(--energy-bright)' : 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: 2 }}>
                      <Icon name="check" style={{ width: 13, height: 13 }} />
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
            )}
            {m.cards && (
              <div style={{ display: 'flex', flexDirection: 'column', marginTop: 32 }}>
                {m.cards.map(({ icon, title, desc }, ci) => (
                  <Reveal key={title} variant="up" delay={ci * 0.07}>
                    <div style={{ display: 'flex', gap: 22, alignItems: 'flex-start', padding: '28px 0', borderBottom: ci < m.cards.length - 1 ? `1px solid ${dark ? 'rgba(255,255,255,0.08)' : 'var(--border-subtle)'}` : 'none' }}>
                      <span style={{ flexShrink: 0, width: 54, height: 54, borderRadius: 15, background: dark ? 'rgba(67,196,67,0.12)' : 'var(--accent-soft)', border: dark ? '1px solid rgba(67,196,67,0.2)' : '1px solid var(--border-subtle)', color: dark ? 'var(--energy-bright)' : 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <Icon name={icon} style={{ width: 26, height: 26 }} />
                      </span>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 18, color: dark ? '#fff' : 'var(--text-strong)', marginBottom: 8, lineHeight: 1.2 }}>{title}</div>
                        <p style={{ margin: 0, fontSize: 15.5, color: dark ? 'rgba(255,255,255,0.72)' : 'var(--text-body)', lineHeight: 1.75 }}>{desc}</p>
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>
            )}
          </div>
        );

        return (
          <section key={m.label} style={{ padding: 'var(--section-y) 0', background: dark ? 'var(--surface-inverse)' : 'var(--surface-page)', color: dark ? '#fff' : 'inherit' }}>
            {imgSrc ? (
              <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', display: 'grid', gap: 64, alignItems: 'stretch' }} className="adr-split">
                {imgLeft ? imagePanel : contentPanel}
                {imgLeft ? contentPanel : imagePanel}
              </div>
            ) : (
              <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 var(--gutter)' }}>
                {contentPanel}
              </div>
            )}
          </section>
        );
      })}

      {/* ── Scope / Delivery grid — just before Why ADIRATH ─────────────── */}
      {s.sections && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead align="center" eyebrow="Delivers" title={`What ADIRATH delivers in ${s.short}.`} max={640} />
            <div style={{ display: 'grid', gap: 18, marginTop: 48 }} className="adr-bento">
              {s.sections.map((label, si) => {
                const [title, sub] = label.split(' — ');
                const icon = s.modes?.[si]?.icon || 'check-circle-2';
                return (
                  <Reveal key={label} variant="up" delay={si * 0.05}>
                    <DeliverCard title={title} sub={sub} icon={icon} dark={false} />
                  </Reveal>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Why ADIRATH + Technical Specs — dark ──────────────────────────── */}
      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', ...(s.hideSpecCard ? {} : { display: 'grid', gap: 48, alignItems: 'start' }) }} className={s.hideSpecCard ? '' : 'adr-split'}>
          <div style={s.hideSpecCard ? { maxWidth: 860 } : {}}>
            <SectionHead dark eyebrow="Why ADIRATH" title="Engineering advantages that compound." max={620} />
            <div className="adr-benefits-grid" style={{ display: 'grid', gap: 20, marginTop: 36 }}>
              {s.benefits.map(([ic, t, d]) => (
                <Reveal key={t} variant="up" style={{ display: 'flex', gap: 14 }}>
                  <span style={{ flexShrink: 0, width: 44, height: 44, borderRadius: 12, background: 'rgba(67,196,67,0.15)', color: 'var(--energy-bright)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}><Icon name={ic} style={{ width: 22, height: 22 }} /></span>
                  <div>
                    <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, color: '#fff', fontSize: 17, marginBottom: 5 }}>{t}</div>
                    <div style={{ fontSize: 15, color: 'rgba(255,255,255,0.75)', lineHeight: 1.6 }}>{d}</div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
          {!s.hideSpecCard && (
            <Reveal variant="scale" style={{ background: 'var(--glass-dark-bg)', border: '1px solid var(--glass-dark-border)', borderRadius: 'var(--radius-xl)', padding: 28 }}>
              <div style={{ fontFamily: 'var(--font-ui)', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', fontSize: 13.5, color: 'var(--energy-bright)', marginBottom: 18 }}>Technical Specs</div>
              {s.specs.map(([k, v], i) => (
                <div key={k} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 0', borderBottom: i < s.specs.length - 1 ? '1px solid var(--border-on-dark)' : 'none' }}>
                  <span style={{ color: 'rgba(255,255,255,0.7)', fontSize: 15 }}>{k}</span>
                  <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: '#fff', fontSize: 15.5, textAlign: 'right' }}>{v}</span>
                </div>
              ))}
              {serviceKey === 'bess' && (
                <button onClick={() => setShowDownload(true)} style={{ marginTop: 20, width: '100%', padding: '13px 0', background: 'rgba(67,196,67,0.1)', border: '1px solid rgba(67,196,67,0.25)', borderRadius: 'var(--radius-lg)', color: 'var(--energy-bright)', fontFamily: 'var(--font-ui)', fontWeight: 700, fontSize: 15, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, transition: 'background var(--dur-fast)' }}>
                  <Icon name="file-down" style={{ width: 15, height: 15 }} /> Download Full Spec Sheet
                </button>
              )}
            </Reveal>
          )}
        </div>
      </section>

      {/* ── Projects Executed — light ──────────────────────────────────────── */}
      {s.projectsExecuted && (
        <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
          <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)' }}>
            <SectionHead align="center" eyebrow="Projects Executed" title="Industrial Automation Capabilities." />
            <p style={{ textAlign: 'center', fontSize: 15.5, color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', fontWeight: 600, letterSpacing: '0.06em', marginTop: 10, marginBottom: 48 }}>
              {s.projectsExecuted.sub}
            </p>
            <div style={{ display: 'grid', gap: 22 }} className="adr-bento">
              {s.projectsExecuted.items.map(({ icon, title, tags }, pi) => (
                <Reveal key={title} variant="up" delay={pi * 0.06}>
                  <div style={{ background: 'var(--surface-card)', border: '1px solid var(--border-subtle)', borderRadius: 'var(--radius-xl)', padding: '28px 24px', height: '100%', display: 'flex', flexDirection: 'column', gap: 18, boxShadow: 'var(--shadow-sm)' }}>
                    <span style={{ width: 52, height: 52, borderRadius: 14, background: 'var(--accent-soft)', color: 'var(--accent)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                      <Icon name={icon} style={{ width: 24, height: 24 }} />
                    </span>
                    <div style={{ fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 16, color: 'var(--text-strong)', lineHeight: 1.3, letterSpacing: '0.01em', textTransform: 'uppercase' }}>{title}</div>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 'auto' }}>
                      {tags.map((t) => (
                        <span key={t} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '5px 12px', borderRadius: 'var(--radius-pill)', background: 'var(--accent-soft)', border: '1px solid var(--border-subtle)', fontSize: 13, color: 'var(--accent)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>
                          <Icon name="check" style={{ width: 11, height: 11 }} />{t}
                        </span>
                      ))}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── FAQ — dark ────────────────────────────────────────────────────── */}
      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-inverse)', color: '#fff' }}>
        <div style={{ maxWidth: 860, margin: '0 auto', padding: '0 var(--gutter)' }}>
          <SectionHead dark align="center" eyebrow="FAQ" title={`${s.short} — common questions`} />
          <div style={{ marginTop: 40 }}>
            {faqs.map(([q, a], i) => <FAQItem key={i} q={q} a={a} />)}
          </div>
        </div>
      </section>

      <section style={{ padding: 'var(--section-y) 0', background: 'var(--surface-page)' }}>
        <div style={{ maxWidth: 'var(--container-xl)', margin: '0 auto', padding: '0 var(--gutter)', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 18 }}>
          <span style={{ color: 'var(--text-muted)', fontFamily: 'var(--font-ui)', fontWeight: 600 }}>Next segment</span>
          <a onClick={() => onNavigate({ page: 'Service', service: nextKey })} style={{ cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 12, fontFamily: 'var(--font-heading)', fontWeight: 700, fontSize: 'var(--fs-h3)', color: 'var(--text-strong)' }}>
            {next.name} <Icon name="arrow-right" style={{ width: 26, height: 26, color: 'var(--accent)' }} />
          </a>
        </div>
      </section>

      {showDownload && <SpecDownloadModal onClose={() => setShowDownload(false)} />}
    </div>
  );
}

export function FAQItem({ q, a }) {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderBottom: '1px solid var(--border-on-dark)' }}>
      <button onClick={() => setOpen((o) => !o)} style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, padding: '20px 0', background: 'none', border: 'none', cursor: 'pointer', textAlign: 'left' }}>
        <span style={{ fontFamily: 'var(--font-heading)', fontWeight: 600, fontSize: 18, color: '#fff' }}>{q}</span>
        <Icon name={open ? 'minus' : 'plus'} style={{ width: 22, height: 22, color: 'var(--energy-bright)', flexShrink: 0 }} />
      </button>
      <div style={{ display: 'grid', gridTemplateRows: open ? '1fr' : '0fr', transition: 'grid-template-rows var(--dur-med) var(--ease-out)' }}>
        <div style={{ overflow: 'hidden' }}>
          <p style={{ margin: 0, padding: '0 0 20px', fontSize: 15.5, color: 'rgba(255,255,255,0.78)', lineHeight: 1.7, maxWidth: 680 }}>{a}</p>
        </div>
      </div>
    </div>
  );
}
